import { buildCopyContextMenuItems, gridHeaderIconsSx, useClearCellRangeOnOutsideClick } from '@/components/ReportDashboard/Shared/GridActionsMenu'
import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { GfrItem } from "@/constants/ReportDashboard/gfr-items"
import { formatAssetClass } from "@/constants/ReportDashboard/asset-class"
import { ReportItem } from "@/constants/ReportDashboard/report-items"
import { getColorWithOpacity } from '@/constants/colors.constant'
import { useHoldingsMetric } from '@/contexts/HoldingsTabContext'
import { useHoldingsSecurityDetails, usePortfolioBaseCCY } from '@/contexts/RADashboardDataContext/hooks'
import { useDarkModeCheck, useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { cardPrefsContextKey, CardProps, HoldingsSecurityDetails, PortfolioHoldingsPrefs } from '@/interfaces/raDashboard.interface'
import { putUserPreference } from '@/services/userPreference.service'
import { getFullHeader, getHeader } from '@/utils/factorAnalytics.util'
import { columnsSideBarPanelDef, filtersSideBarPanelDef, getGridThemeClass, sideBarDefaultConfig, sortColumnGroupsByName } from '@/utils/grid-utils/ag-grid-config'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import {
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnRowGroupChangedEvent,
  ColumnState,
  GridReadyEvent,
  IAggFunc,
  IAggFuncParams,
  IRowNode,
  ModuleRegistry,
  SelectionChangedEvent,
  SideBarDef,
  ToolPanelVisibleChangedEvent,
  ValueFormatterFunc,
  ValueFormatterParams
} from '@ag-grid-community/core'
import { CsvExportModule } from '@ag-grid-community/csv-export'
import { AgGridReact } from '@ag-grid-community/react'
import { ClipboardModule } from '@ag-grid-enterprise/clipboard'
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel'
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export'
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel'
import { MenuModule } from '@ag-grid-enterprise/menu'
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection'
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { cardStyle } from '../../../styles/reportDashboardStyles'

ModuleRegistry.registerModules([ClientSideRowModelModule, RowGroupingModule, ColumnsToolPanelModule, FiltersToolPanelModule, CsvExportModule, ExcelExportModule, ClipboardModule, RangeSelectionModule, MenuModule])

// MUI FilterAltIcon SVG, replaces ag-grid's default 3-lines filter glyph
const FILTER_ICON_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7 6h10l-5.01 6.3L7 6zm-2.75-.39C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39c.51-.66.04-1.61-.79-1.61H5.04c-.83 0-1.3.95-.79 1.61z"/></svg>'


// Returns a parsed dayjs instance or null when the value is missing or unparseable.
// Used by the date columns so invalid date strings don't poison sort/filter with NaN.
const parseDate = (value: string | null | undefined): dayjs.Dayjs | null => {
  if (value == null) return null
  const d = dayjs(value)
  return d.isValid() ? d : null
}

const toLocaleNum = (value: any | null, decimalPlaces?: number) => {
  if (value == null) return null;
  return Number(value).toLocaleString(navigator.language, decimalPlaces || decimalPlaces === 0 ? { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces } : {});
}

const toFixedNum = (value: any | null, decimalPlaces?: number) => {
  if (value == null) return null;
  return Number(value).toFixed(decimalPlaces)
}

const currencyFormatter = new Intl.NumberFormat(navigator.language, {
  style: "currency",
  currency: "USD"
})

// Date columns: emit date only (raw value is an ISO datetime).
const DATE_FIELDS = new Set<string>(['maturity', ReportItem.DatedDate])

// Raw (unformatted) cell value — shared by export and copy.
const rawCellValue = (params: any) => {
  const field = params?.column?.getColDef?.()?.field
  const data = params?.node?.data
  const raw = field && data && data[field] !== undefined ? data[field] : params.value
  if (field && DATE_FIELDS.has(field)) {
    const d = parseDate(raw)
    return d ? d.format('YYYY-MM-DD') : raw
  }
  return raw
}

function blankIfNull(strings: TemplateStringsArray, valueExp: string | null): string {
  if (valueExp == null) return '';
  if (strings.length == 0) return valueExp;
  if (strings.length == 1) return strings[0];
  return strings.join(valueExp);
}

function currencyValueFormatter(p: ValueFormatterParams<HoldingsSecurityDetails, any>): string {
  return blankIfNull`${toLocaleNum(p.value, 2)}`
}

function fixedValueFormatter(decimalPlaces?: number): ValueFormatterFunc<HoldingsSecurityDetails, any> {
  return (p: ValueFormatterParams<HoldingsSecurityDetails, any>) => blankIfNull`${toLocaleNum(p.value, decimalPlaces)}`;
}

const defaultDecimalFormatter = fixedValueFormatter(2);

function percentValueFormatter(p: ValueFormatterParams<HoldingsSecurityDetails, any>): string {
  return blankIfNull`${toFixedNum(p.value, 2)}%`
}

function assetClassValueFormatter(p: ValueFormatterParams<HoldingsSecurityDetails, any>): string {
  return formatAssetClass(p.value)
}

const NUMERIC_CELL = 'ag-right-aligned-cell'

const COLUMN_DEFS: ColDef<HoldingsSecurityDetails>[] = [
  // These are shown by default
  { field: 'identifier',         headerName: getHeader(ReportItem.StandardID), pinned: 'left', minWidth: 130 },
  { field: 'issuer',             headerName: getHeader(ReportItem.Description), minWidth: 160,  enableRowGroup: true },
  { field: ReportItem.AssetClass, headerName: getHeader(ReportItem.AssetClass), minWidth: 120, cellDataType: 'string', enableRowGroup: true, valueFormatter: assetClassValueFormatter },
  { field: ReportItem.PctMarketValue, headerName: getHeader(ReportItem.PctMarketValue), headerTooltip: getFullHeader(ReportItem.PctMarketValue), minWidth: 120, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "sum" },
  { field: GfrItem.ModelRisk, headerName: getHeader(GfrItem.ModelRisk), minWidth: 120, cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.SpecificRisk, headerName: getHeader(GfrItem.SpecificRisk), minWidth: 120, cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.SecurityRisk, headerName: getHeader(GfrItem.SecurityRisk), minWidth: 120, cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.SecurityRiskContrib, headerName: getHeader(GfrItem.SecurityRiskContrib), headerTooltip: getFullHeader(GfrItem.SecurityRiskContrib), minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: 'duration',           headerName: getHeader(ReportItem.EffectiveDuration), headerTooltip: getFullHeader(ReportItem.EffectiveDuration), minWidth: 100, cellClass: NUMERIC_CELL, aggFunc: 'notional_sum', valueFormatter: defaultDecimalFormatter },
  { field: 'spread',             headerName: getHeader(ReportItem.OAS), minWidth: 120, cellClass: NUMERIC_CELL, aggFunc: 'notional_sum', valueFormatter: defaultDecimalFormatter },
  // The following are hidden by default
  { field: 'holdings',           headerName: getHeader(ReportItem.Holdings), hide: true, minWidth: 100, cellClass: NUMERIC_CELL, valueFormatter: fixedValueFormatter() },
  { field: 'notionalWeight',     headerName: getHeader(ReportItem.PctFaceMarketValue), headerTooltip: getFullHeader(ReportItem.PctFaceMarketValue), hide: true, minWidth: 100,  cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: 'coupon',             headerName: getHeader(ReportItem.CouponRate), headerTooltip: getFullHeader(ReportItem.CouponRate), hide: true, minWidth: 90, cellClass: NUMERIC_CELL, aggFunc: 'par_sum', valueFormatter: defaultDecimalFormatter },
  {
    field: 'maturity',
    hide: true, // Hidden by default
    headerName: getHeader(ReportItem.MaturityDate),
    minWidth: 110,
    filter: 'agDateColumnFilter',
    filterParams: {
      // ag-grid passes a Date for filterDate and the raw cell value (string) for cellValue.
      // Compare by day so the date filter UI matches the displayed YYYY-MM-DD precision.
      // Treat null and unparseable strings the same — sink them below the filter (return -1).
      comparator: (filterDate: Date, cellValue: string | null) => {
        const cell = parseDate(cellValue)
        if (cell == null) return -1
        const filter = dayjs(filterDate).startOf('day')
        const cellDay = cell.startOf('day')
        if (cellDay.isSame(filter)) return 0
        return cellDay.isBefore(filter) ? -1 : 1
      },
    },
    // Sort chronologically — null and unparseable values sink to the bottom (avoids NaN from dayjs().valueOf()).
    comparator: (a: string | null, b: string | null) => {
      const da = parseDate(a)
      const db = parseDate(b)
      if (da == null && db == null) return 0
      if (da == null) return 1
      if (db == null) return -1
      return da.valueOf() - db.valueOf()
    },
    valueFormatter: p => {
      const d = parseDate(p.value)
      return d ? d.format('YYYY-MM-DD') : ''
    },
  },
  { field: ReportItem.Price, headerName: getHeader(ReportItem.Price), headerTooltip: getFullHeader(ReportItem.Price), hide: true, minWidth: 90, cellClass: NUMERIC_CELL, aggFunc: 'par_sum', valueFormatter: currencyValueFormatter },
  { field: 'marketValue', headerValueGetter: (p) => { const { baseCCY } = p.context; return `Market Value - ${baseCCY}`}, hide: true, minWidth: 145,  cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: currencyValueFormatter },
  { field: GfrItem.CountryRiskContrib, headerName: getHeader(GfrItem.CountryRiskContrib), headerTooltip: getFullHeader(GfrItem.CountryRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.FundamentalRiskContrib, headerName: getHeader(GfrItem.FundamentalRiskContrib), headerTooltip: getFullHeader(GfrItem.FundamentalRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.IndustryRiskContrib, headerName: getHeader(GfrItem.IndustryRiskContrib), headerTooltip: getFullHeader(GfrItem.IndustryRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.RegionRiskContrib, headerName: getHeader(GfrItem.RegionRiskContrib), headerTooltip: getFullHeader(GfrItem.RegionRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.OtherSpreadRiskContrib, headerName: getHeader(GfrItem.OtherSpreadRiskContrib), headerTooltip: getFullHeader(GfrItem.OtherSpreadRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.QualityRiskContrib, headerName: getHeader(GfrItem.QualityRiskContrib), headerTooltip: getFullHeader(GfrItem.QualityRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.SectorRiskContrib, headerName: getHeader(GfrItem.SectorRiskContrib), headerTooltip: getFullHeader(GfrItem.SectorRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.YieldCurveRiskContrib, headerName: getHeader(GfrItem.YieldCurveRiskContrib), headerTooltip: getFullHeader(GfrItem.YieldCurveRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.AltRiskContrib, headerName: getHeader(GfrItem.AltRiskContrib), headerTooltip: getFullHeader(GfrItem.AltRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.CommodityRiskContrib, headerName: getHeader(GfrItem.CommodityRiskContrib), headerTooltip: getFullHeader(GfrItem.CommodityRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.SpecificRiskContrib, headerName: getHeader(GfrItem.SpecificRiskContrib), headerTooltip: getFullHeader(GfrItem.SpecificRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.ModelRiskContrib, headerName: getHeader(GfrItem.ModelRiskContrib), headerTooltip: getFullHeader(GfrItem.ModelRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: GfrItem.CurrencyRiskContrib, headerName: getHeader(GfrItem.CurrencyRiskContrib), headerTooltip: getFullHeader(GfrItem.CurrencyRiskContrib), hide: true, minWidth: 165, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: 'sector',             headerName: 'Sector', hide: true,   minWidth: 120,  enableRowGroup: true },
  { field: 'country',            headerName: 'Country', hide: true,  minWidth: 130,  enableRowGroup: true },
  { field: 'currency',           headerName: 'Currency', hide: true, minWidth: 100,  enableRowGroup: true },
  { field: 'rating',             headerName: getHeader(ReportItem.MoodyQuality), headerTooltip: getFullHeader(ReportItem.MoodyQuality), hide: true, minWidth: 90, enableRowGroup: true },
  { field: 'yearsToMaturity',    headerName: getHeader(ReportItem.YearsToMaturity), headerTooltip: getFullHeader(ReportItem.YearsToMaturity), hide: true, minWidth: 150, aggFunc: "notional_sum", cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter },
  { field: 'dv01',               headerName: 'DV01', hide: true, minWidth: 90, cellClass: NUMERIC_CELL, aggFunc: 'sum', valueFormatter: defaultDecimalFormatter },
  { field: 'convexity',          headerName: getHeader(ReportItem.Convexity), hide: true, minWidth: 100,  cellClass: NUMERIC_CELL, aggFunc: "notional_sum", valueFormatter: defaultDecimalFormatter },
  { field: 'yieldToMaturity',    headerName: getHeader(ReportItem.YieldToMaturity), headerTooltip: getFullHeader(ReportItem.YieldToMaturity), hide: true, minWidth: 80, cellClass: NUMERIC_CELL, aggFunc: "sum", valueFormatter: defaultDecimalFormatter },
  { field: ReportItem.SpreadDuration, headerName: getHeader(ReportItem.SpreadDuration), headerTooltip: getFullHeader(ReportItem.SpreadDuration), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.YieldToWorst, headerName: getHeader(ReportItem.YieldToWorst), headerTooltip: getFullHeader(ReportItem.YieldToWorst), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.DividendYield, headerName: getHeader(ReportItem.DividendYield), headerTooltip: getFullHeader(ReportItem.DividendYield), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.AverageLife, headerName: getHeader(ReportItem.AverageLife), headerTooltip: getFullHeader(ReportItem.AverageLife), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.Beta, headerName: getHeader(ReportItem.Beta), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.BookToPrice, headerName: getHeader(ReportItem.BookToPrice), headerTooltip: getFullHeader(ReportItem.BookToPrice), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.BookValue, headerName: getHeader(ReportItem.BookValue), headerTooltip: getFullHeader(ReportItem.BookValue), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.CashFlowYield, headerName: getHeader(ReportItem.CashFlowYield), headerTooltip: getFullHeader(ReportItem.CashFlowYield), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.CurrentYield, headerName: getHeader(ReportItem.CurrentYield), headerTooltip: getFullHeader(ReportItem.CurrentYield), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "par_sum" },
  { field: ReportItem.EPRatio, headerName: getHeader(ReportItem.EPRatio), headerTooltip: getFullHeader(ReportItem.EPRatio), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.EarningsPerShare, headerName: getHeader(ReportItem.EarningsPerShare), headerTooltip: getFullHeader(ReportItem.EarningsPerShare), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.EffectiveD2, headerName: getHeader(ReportItem.EffectiveD2), headerTooltip: getFullHeader(ReportItem.EffectiveD2), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.EffectiveD3, headerName: getHeader(ReportItem.EffectiveD3), headerTooltip: getFullHeader(ReportItem.EffectiveD3), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.DurContributionPct, headerName: getHeader(ReportItem.DurContributionPct), headerTooltip: getFullHeader(ReportItem.DurContributionPct), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "sum" },
  { field: ReportItem.DurContributionYrs, headerName: getHeader(ReportItem.DurContributionYrs), headerTooltip: getFullHeader(ReportItem.DurContributionYrs), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "sum" },
  { field: ReportItem.EffectiveSpreadDuration, headerName: getHeader(ReportItem.EffectiveSpreadDuration), headerTooltip: getFullHeader(ReportItem.EffectiveSpreadDuration), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.SpreadDurContributionPct, headerName: getHeader(ReportItem.SpreadDurContributionPct), headerTooltip: getFullHeader(ReportItem.SpreadDurContributionPct), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "sum" },
  { field: ReportItem.SpreadDurContributionYrs, headerName: getHeader(ReportItem.SpreadDurContributionYrs), headerTooltip: getFullHeader(ReportItem.SpreadDurContributionYrs), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "sum" },
  { field: ReportItem.GainLoss, headerName: getHeader(ReportItem.GainLoss), headerTooltip: getFullHeader(ReportItem.GainLoss), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "sum" },
  { field: ReportItem.MacaulayDuration, headerName: getHeader(ReportItem.MacaulayDuration), headerTooltip: getFullHeader(ReportItem.MacaulayDuration), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.MarketCap, headerName: getHeader(ReportItem.MarketCap), headerTooltip: getFullHeader(ReportItem.MarketCap), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.MarketCapLog, headerName: getHeader(ReportItem.MarketCapLog), headerTooltip: getFullHeader(ReportItem.MarketCapLog), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "sum" },
  { field: ReportItem.MarketValuePar, headerName: getHeader(ReportItem.MarketValuePar), headerTooltip: getFullHeader(ReportItem.MarketValuePar), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "sum" },
  { field: ReportItem.ModifiedDuration, headerName: getHeader(ReportItem.ModifiedDuration), headerTooltip: getFullHeader(ReportItem.ModifiedDuration), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.Momentum, headerName: getHeader(ReportItem.Momentum), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.NotionalValue, headerName: getHeader(ReportItem.NotionalValue), headerTooltip: getFullHeader(ReportItem.NotionalValue), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "sum" },
  { field: ReportItem.PBRatio, headerName: getHeader(ReportItem.PBRatio), headerTooltip: getFullHeader(ReportItem.PBRatio), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.PERatio, headerName: getHeader(ReportItem.PERatio), headerTooltip: getFullHeader(ReportItem.PERatio), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.SPQuality, headerName: getHeader(ReportItem.SPQuality), headerTooltip: getFullHeader(ReportItem.SPQuality), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true, aggFunc: "notional_sum" },
  { field: ReportItem.SecurityCount, headerName: getHeader(ReportItem.SecurityCount), headerTooltip: getFullHeader(ReportItem.SecurityCount), minWidth: 120, hide: true, cellDataType: 'number', aggFunc: "sum", cellClass: NUMERIC_CELL, valueFormatter: fixedValueFormatter(0) },
  { field: ReportItem.BaseTotalAccruedInterest, headerName: getHeader(ReportItem.BaseTotalAccruedInterest), headerTooltip: getFullHeader(ReportItem.BaseTotalAccruedInterest), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "sum" },
  { field: ReportItem.TrailingEPS, headerName: getHeader(ReportItem.TrailingEPS), headerTooltip: getFullHeader(ReportItem.TrailingEPS), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.Volatility, headerName: getHeader(ReportItem.Volatility), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.YearsToWorst, headerName: getHeader(ReportItem.YearsToWorst), headerTooltip: getFullHeader(ReportItem.YearsToWorst), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.MSCICountry, headerName: getHeader(ReportItem.MSCICountry), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.GICSIndustryGrp, headerName: getHeader(ReportItem.GICSIndustryGrp), headerTooltip: getFullHeader(ReportItem.GICSIndustryGrp), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.GICSIndustry, headerName: getHeader(ReportItem.GICSIndustry), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.GICSSector, headerName: getHeader(ReportItem.GICSSector), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.GICSSubIndustry, headerName: getHeader(ReportItem.GICSSubIndustry), headerTooltip: getFullHeader(ReportItem.GICSSubIndustry), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.ReturnOnEquity, headerName: getHeader(ReportItem.ReturnOnEquity), headerTooltip: getFullHeader(ReportItem.ReturnOnEquity), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.DatedDate, headerName: getHeader(ReportItem.DatedDate), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true, valueFormatter: p => { const d = parseDate(p.value); return d ? d.format('YYYY-MM-DD') : '' } },
  { field: ReportItem.KeyRate1Month, headerName: getHeader(ReportItem.KeyRate1Month), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate1Year, headerName: getHeader(ReportItem.KeyRate1Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate2Year, headerName: getHeader(ReportItem.KeyRate2Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate3Month, headerName: getHeader(ReportItem.KeyRate3Month), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate3Year, headerName: getHeader(ReportItem.KeyRate3Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate4Year, headerName: getHeader(ReportItem.KeyRate4Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate5Year, headerName: getHeader(ReportItem.KeyRate5Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate6Month, headerName: getHeader(ReportItem.KeyRate6Month), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate6Year, headerName: getHeader(ReportItem.KeyRate6Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate7Year, headerName: getHeader(ReportItem.KeyRate7Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate8Year, headerName: getHeader(ReportItem.KeyRate8Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate9Month, headerName: getHeader(ReportItem.KeyRate9Month), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate9Year, headerName: getHeader(ReportItem.KeyRate9Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate10Year, headerName: getHeader(ReportItem.KeyRate10Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate15Year, headerName: getHeader(ReportItem.KeyRate15Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate0Year, headerName: getHeader(ReportItem.KeyRate0Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate25Year, headerName: getHeader(ReportItem.KeyRate25Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.KeyRate30Year, headerName: getHeader(ReportItem.KeyRate30Year), minWidth: 120, hide: true, cellDataType: 'number', cellClass: NUMERIC_CELL, valueFormatter: defaultDecimalFormatter, aggFunc: "notional_sum" },
  { field: ReportItem.AssetType, headerName: getHeader(ReportItem.AssetType), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.CountryofIncorp, headerName: getHeader(ReportItem.CountryofIncorp), headerTooltip: getFullHeader(ReportItem.CountryofIncorp), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.CountryofRisk, headerName: getHeader(ReportItem.CountryofRisk), headerTooltip: getFullHeader(ReportItem.CountryofRisk), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.BaseCurrencyName, headerName: getHeader(ReportItem.BaseCurrencyName), headerTooltip: getFullHeader(ReportItem.BaseCurrencyName), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.CUSIP9, headerName: getHeader(ReportItem.CUSIP9), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.ExchangeTicker, headerName: getHeader(ReportItem.ExchangeTicker), headerTooltip: getFullHeader(ReportItem.ExchangeTicker), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.ISIN12, headerName: getHeader(ReportItem.ISIN12), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true },
  { field: ReportItem.SEDOL7, headerName: getHeader(ReportItem.SEDOL7), minWidth: 120, hide: true, cellDataType: 'string', enableRowGroup: true }
]

const TOOLS_COLUMN_DEFS = sortColumnGroupsByName<HoldingsSecurityDetails>([
  { headerName: "Security Details", children: [
    { field: 'identifier',         headerName: getHeader(ReportItem.StandardID) },
    { field: 'issuer',             headerName: getHeader(ReportItem.Description) },
    { field: 'holdings',           headerName: 'Holdings' },
    { field: 'sector',             headerName: getHeader(ReportItem.SectorName) },
    { field: 'country',            headerName: getHeader(ReportItem.CountryCode) },
    { field: 'currency',           headerName: 'Currency' },
    { field: 'price',              headerName: getHeader(ReportItem.Price) },
    { field: 'notionalWeight',     headerName: getHeader(ReportItem.PctFaceMarketValue) },
    { field: ReportItem.NotionalValue, headerName: getHeader(ReportItem.NotionalValue) },
    { field: ReportItem.PctMarketValue, headerName: getHeader(ReportItem.PctMarketValue) },
    { field: ReportItem.SecurityCount, headerName: getHeader(ReportItem.SecurityCount) },
    { field: ReportItem.AssetType, headerName: getHeader(ReportItem.AssetType) },
    { field: ReportItem.AssetClass, headerName: getHeader(ReportItem.AssetClass) },
    { field: ReportItem.CountryofIncorp, headerName: getHeader(ReportItem.CountryofIncorp) },
    { field: ReportItem.CountryofRisk, headerName: getHeader(ReportItem.CountryofRisk) },
    { field: ReportItem.BaseCurrencyName, headerName: getHeader(ReportItem.BaseCurrencyName) },
    { field: ReportItem.CUSIP9, headerName: getHeader(ReportItem.CUSIP9) },
    { field: ReportItem.ExchangeTicker, headerName: getHeader(ReportItem.ExchangeTicker) },
    { field: ReportItem.ISIN12, headerName: getHeader(ReportItem.ISIN12) },
    { field: ReportItem.SEDOL7, headerName: getHeader(ReportItem.SEDOL7) }
  ]},
  { headerName: "Risk", children: [
    { field: GfrItem.CountryRiskContrib, headerName: getHeader(GfrItem.CountryRiskContrib) },
    { field: GfrItem.FundamentalRiskContrib, headerName: getHeader(GfrItem.FundamentalRiskContrib) },
    { field: GfrItem.IndustryRiskContrib, headerName: getHeader(GfrItem.IndustryRiskContrib) },
    { field: GfrItem.RegionRiskContrib, headerName: getHeader(GfrItem.RegionRiskContrib) },
    { field: GfrItem.OtherSpreadRiskContrib, headerName: getHeader(GfrItem.OtherSpreadRiskContrib) },
    { field: GfrItem.QualityRiskContrib, headerName: getHeader(GfrItem.QualityRiskContrib) },
    { field: GfrItem.SectorRiskContrib, headerName: getHeader(GfrItem.SectorRiskContrib) },
    { field: GfrItem.YieldCurveRiskContrib, headerName: getHeader(GfrItem.YieldCurveRiskContrib) },
    { field: GfrItem.AltRiskContrib, headerName: getHeader(GfrItem.AltRiskContrib) },
    { field: GfrItem.CommodityRiskContrib, headerName: getHeader(GfrItem.CommodityRiskContrib) },
    { field: GfrItem.SpecificRiskContrib, headerName: getHeader(GfrItem.SpecificRiskContrib) },
    { field: GfrItem.ModelRiskContrib, headerName: getHeader(GfrItem.ModelRiskContrib) },
    { field: GfrItem.CurrencyRiskContrib, headerName: getHeader(GfrItem.CurrencyRiskContrib) },
    { field: GfrItem.SecurityRiskContrib, headerName: getHeader(GfrItem.SecurityRiskContrib) },
    { field: GfrItem.SecurityRisk, headerName: getHeader(GfrItem.SecurityRisk) },
    { field: GfrItem.ModelRisk, headerName: getHeader(GfrItem.ModelRisk) },
    { field: GfrItem.SpecificRisk, headerName: getHeader(GfrItem.SpecificRisk) },
  ]},
  { headerName: "Equity", children: [
    { field: ReportItem.DividendYield, headerName: getHeader(ReportItem.DividendYield) },
    { field: ReportItem.Beta, headerName: getHeader(ReportItem.Beta) },
    { field: ReportItem.BookToPrice, headerName: getHeader(ReportItem.BookToPrice) },
    { field: ReportItem.EPRatio, headerName: getHeader(ReportItem.EPRatio) },
    { field: ReportItem.EarningsPerShare, headerName: getHeader(ReportItem.EarningsPerShare) },
    { field: ReportItem.MarketCap, headerName: getHeader(ReportItem.MarketCap) },
    { field: ReportItem.MarketCapLog, headerName: getHeader(ReportItem.MarketCapLog) },
    { field: ReportItem.Momentum, headerName: getHeader(ReportItem.Momentum) },
    { field: ReportItem.PBRatio, headerName: getHeader(ReportItem.PBRatio) },
    { field: ReportItem.PERatio, headerName: getHeader(ReportItem.PERatio) },
    { field: ReportItem.TrailingEPS, headerName: getHeader(ReportItem.TrailingEPS) },
    { field: ReportItem.Volatility, headerName: getHeader(ReportItem.Volatility) },
    { field: ReportItem.MSCICountry, headerName: getHeader(ReportItem.MSCICountry) },
    { field: ReportItem.GICSIndustryGrp, headerName: getHeader(ReportItem.GICSIndustryGrp) },
    { field: ReportItem.GICSIndustry, headerName: getHeader(ReportItem.GICSIndustry) },
    { field: ReportItem.GICSSector, headerName: getHeader(ReportItem.GICSSector) },
    { field: ReportItem.GICSSubIndustry, headerName: getHeader(ReportItem.GICSSubIndustry) },
    { field: ReportItem.ReturnOnEquity, headerName: getHeader(ReportItem.ReturnOnEquity) }
  ]},
  { headerName: "Fixed Income", children: [
    { field: 'coupon',             headerName: getHeader(ReportItem.CouponRate) },
    { field: 'maturity',           headerName: getHeader(ReportItem.MaturityDate) },
    { field: 'marketValue',        headerName: getHeader(ReportItem.MarketValue) },
    { field: 'duration',           headerName: 'Duration' },
    { field: 'rating',             headerName: getHeader(ReportItem.MoodyQuality) },
    { field: 'yearsToMaturity',    headerName: getHeader(ReportItem.YearsToMaturity) },
    { field: 'spread',             headerName: getHeader(ReportItem.OAS) },
    { field: 'dv01',               headerName: 'DV01' },
    { field: 'convexity',          headerName: getHeader(ReportItem.Convexity) },
    { field: 'yieldToMaturity',    headerName: getHeader(ReportItem.YieldToMaturity) },
    { field: ReportItem.SpreadDuration, headerName: getHeader(ReportItem.SpreadDuration) },
    { field: ReportItem.YieldToWorst, headerName: getHeader(ReportItem.YieldToWorst) },
    { field: ReportItem.AverageLife, headerName: getHeader(ReportItem.AverageLife) },
    { field: ReportItem.BookValue, headerName: getHeader(ReportItem.BookValue) },
    { field: ReportItem.CashFlowYield, headerName: getHeader(ReportItem.CashFlowYield) },
    { field: ReportItem.CurrentYield, headerName: getHeader(ReportItem.CurrentYield) },
    { field: ReportItem.EffectiveD2, headerName: getHeader(ReportItem.EffectiveD2) },
    { field: ReportItem.EffectiveD3, headerName: getHeader(ReportItem.EffectiveD3) },
    { field: ReportItem.DurContributionPct, headerName: getHeader(ReportItem.DurContributionPct) },
    { field: ReportItem.DurContributionYrs, headerName: getHeader(ReportItem.DurContributionYrs) },
    { field: ReportItem.EffectiveSpreadDuration, headerName: getHeader(ReportItem.EffectiveSpreadDuration) },
    { field: ReportItem.SpreadDurContributionPct, headerName: getHeader(ReportItem.SpreadDurContributionPct) },
    { field: ReportItem.SpreadDurContributionYrs, headerName: getHeader(ReportItem.SpreadDurContributionYrs) },
    { field: ReportItem.GainLoss, headerName: getHeader(ReportItem.GainLoss) },
    { field: ReportItem.MacaulayDuration, headerName: getHeader(ReportItem.MacaulayDuration) },
    { field: ReportItem.MarketValuePar, headerName: getHeader(ReportItem.MarketValuePar) },
    { field: ReportItem.ModifiedDuration, headerName: getHeader(ReportItem.ModifiedDuration) },
    { field: ReportItem.SPQuality, headerName: getHeader(ReportItem.SPQuality) },
    { field: ReportItem.BaseTotalAccruedInterest, headerName: getHeader(ReportItem.BaseTotalAccruedInterest) },
    { field: ReportItem.YearsToWorst, headerName: getHeader(ReportItem.YearsToWorst) },
    { field: ReportItem.DatedDate, headerName: getHeader(ReportItem.DatedDate) },
    { field: ReportItem.KeyRate1Month, headerName: getHeader(ReportItem.KeyRate1Month) },
    { field: ReportItem.KeyRate1Year, headerName: getHeader(ReportItem.KeyRate1Year) },
    { field: ReportItem.KeyRate2Year, headerName: getHeader(ReportItem.KeyRate2Year) },
    { field: ReportItem.KeyRate3Month, headerName: getHeader(ReportItem.KeyRate3Month) },
    { field: ReportItem.KeyRate3Year, headerName: getHeader(ReportItem.KeyRate3Year) },
    { field: ReportItem.KeyRate4Year, headerName: getHeader(ReportItem.KeyRate4Year) },
    { field: ReportItem.KeyRate5Year, headerName: getHeader(ReportItem.KeyRate5Year) },
    { field: ReportItem.KeyRate6Month, headerName: getHeader(ReportItem.KeyRate6Month) },
    { field: ReportItem.KeyRate6Year, headerName: getHeader(ReportItem.KeyRate6Year) },
    { field: ReportItem.KeyRate7Year, headerName: getHeader(ReportItem.KeyRate7Year) },
    { field: ReportItem.KeyRate8Year, headerName: getHeader(ReportItem.KeyRate8Year) },
    { field: ReportItem.KeyRate9Month, headerName: getHeader(ReportItem.KeyRate9Month) },
    { field: ReportItem.KeyRate9Year, headerName: getHeader(ReportItem.KeyRate9Year) },
    { field: ReportItem.KeyRate10Year, headerName: getHeader(ReportItem.KeyRate10Year) },
    { field: ReportItem.KeyRate15Year, headerName: getHeader(ReportItem.KeyRate15Year) },
    { field: ReportItem.KeyRate0Year, headerName: getHeader(ReportItem.KeyRate0Year) },
    { field: ReportItem.KeyRate25Year, headerName: getHeader(ReportItem.KeyRate25Year) },
    { field: ReportItem.KeyRate30Year, headerName: getHeader(ReportItem.KeyRate30Year) }
  ]},
])

const HoldingsSidebarDef: SideBarDef = {
  ...sideBarDefaultConfig,
  toolPanels: [
    {
      ...columnsSideBarPanelDef,
      toolPanelParams: {
        ...columnsSideBarPanelDef.toolPanelParams,
        suppressSyncLayoutWithGrid: true,  // ✅ decouples tool panel from grid layout
        columnLayout: TOOLS_COLUMN_DEFS,
      }
    },
    filtersSideBarPanelDef
  ]
};


const EMPTY_ARRAY: HoldingsSecurityDetails[] = [];
const PortfolioHoldings: React.FC<CardProps> = (props) => {
  const theme = useTheme()
  const isDarkMode = useDarkModeCheck()
  const gridRef = useRef<AgGridReact<HoldingsSecurityDetails>>(null)
  const gridBoxRef = useRef<HTMLDivElement>(null)
  useClearCellRangeOnOutsideClick(gridBoxRef, () => gridRef.current?.api)
  const gridContextMenuItems = useMemo(
    () => buildCopyContextMenuItems({ processCell: rawCellValue }),
    [],
  )

  const baseCCY = usePortfolioBaseCCY();
  const securityDetails = useHoldingsSecurityDetails();
  const {
    highlightSecurities,
    clearHighlightedSecurities,
    setHighlightGroups
  } = useHoldingsMetric();

  const [collapsed, setCollapsed] = useState(false)
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null,
  )

  const handleExport = (type: 'CSV' | 'EXCEL') => {
    const api = gridRef.current?.api
    if (api) {
      // Export the raw underlying values (editable cells), not the display format.
      if (type === 'EXCEL')
        api.exportDataAsExcel({ fileName: 'portfolio_holdings', processCellCallback: rawCellValue })
      else
        api.exportDataAsCsv({ fileName: 'portfolio_holdings.csv', processCellCallback: rawCellValue })
    }
    setExportMenuAnchor(null)
  }

  // Stable refs so save/apply callbacks never need to be recreated
  const cardPrefsRef         = useRef(props.cardPrefs)
  const onCardPrefsChangeRef = useRef(props.onCardPrefsChange)
  useEffect(() => { cardPrefsRef.current         = props.cardPrefs         }, [props.cardPrefs])
  useEffect(() => { onCardPrefsChangeRef.current = props.onCardPrefsChange }, [props.onCardPrefsChange])

  // Track which dashboard we've already applied prefs for so saves don't re-trigger apply
  const initializedForDashboard = useRef<string | null>(null)
  // Suppress save events that fire async from applyColumnState / setFilterModel
  const applyingRef = useRef(false)

  const applyGridPrefs = useCallback((prefs: PortfolioHoldingsPrefs) => {
    const api = gridRef.current?.api
    if (!api) return
    applyingRef.current = true
    api.getToolPanelInstance('columns')?.setColumnLayout(TOOLS_COLUMN_DEFS);
    if (prefs.columnState) {
      api.applyColumnState({ state: prefs.columnState as unknown as ColumnState[], applyOrder: true })
    }
    if (prefs.filterModel) {
      api.setFilterModel(prefs.filterModel)
    }
    // Defer the reset so async events from applyColumnState are still suppressed
    setTimeout(() => { applyingRef.current = false;  }, 0)
  }, [])

  const onGridReady = useCallback((_e: GridReadyEvent) => {
    const prefs = cardPrefsRef.current
    const saved = prefs?.holdingsPortfolio
    if (saved) {
      initializedForDashboard.current = prefs.dashboardName ?? null
      applyGridPrefs(saved)
    }
  }, [applyGridPrefs])

  // Apply only when the dashboard changes (not on every save)
  useEffect(() => {
    const dashboardName = props.cardPrefs?.dashboardName
    const saved = props.cardPrefs?.holdingsPortfolio
    if (saved && gridRef.current?.api && dashboardName !== initializedForDashboard.current) {
      initializedForDashboard.current = dashboardName ?? null
      applyGridPrefs(saved)
    }
  }, [props.cardPrefs, applyGridPrefs])

  const saveGridState = useCallback(() => {
    if (applyingRef.current) return
    const api  = gridRef.current?.api
    const prefs = cardPrefsRef.current
    if (!api || !prefs?.dashboardName) return
    const newPrefs: PortfolioHoldingsPrefs = {
      columnState: api.getColumnState() as unknown as Record<string, unknown>[],
      filterModel: api.getFilterModel() as Record<string, unknown>,
    }
    const updated = { ...prefs, holdingsPortfolio: newPrefs }
    onCardPrefsChangeRef.current?.(updated)
    putUserPreference(cardPrefsContextKey(prefs.dashboardName), updated)
  }, [])

  const onColumnMoved = useCallback((e: ColumnMovedEvent) => {
    if (e.finished) saveGridState()
  }, [saveGridState])

  const onColumnResized = useCallback((e: ColumnResizedEvent) => {
    if (e.finished) saveGridState()
  }, [saveGridState])

  const onToolPanelVisibleChanged = useCallback((e: ToolPanelVisibleChangedEvent) => {
    if (!e.visible) saveGridState()
  }, [saveGridState])

  const defaultColDef: ColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    filter: true,
  }), [])

  const autoGroupColumnDef: ColDef = useMemo(() => ({
    headerName: 'Group',
    minWidth: 220,
    pinned: 'left',
    checkboxSelection: ({node})=> !!node.group
  }), [])

  const aggFuncs: Record<string, IAggFunc<HoldingsSecurityDetails>> = useMemo(() => {
    return {
      "notional_sum": (params: IAggFuncParams<HoldingsSecurityDetails, number>) => {
        const rows = params.rowNode.childrenAfterGroup;
        if (!rows) return null;
        let sum = 0;
        params.values.forEach((value, index) => {
          const row = rows[index];
          const { data } = row;
          if (data) {
            sum += (data.notionalWeight ?? 0) * (value ?? 0);
          }
        });
        sum /= 100;
        return sum;
      },
      "par_sum": (params: IAggFuncParams<HoldingsSecurityDetails, number>) => {
        const rows = params.rowNode.childrenAfterGroup;
        if (!rows) return null;
        let sum = 0;
        params.values.forEach((value, index) => {
          const row = rows[index];
          const { data } = row;
          if (data) {
            sum += (data.parWeight ?? 0) * (value ?? 0);
          }
        });
        sum /= 100;
        return sum;
      }
    }
  }, []);

  const onColumnRowGroupChanged = useCallback((e: ColumnRowGroupChangedEvent<HoldingsSecurityDetails>) => {
    clearHighlightedSecurities();
    saveGridState()
  }, [saveGridState])

  const onRowSelectionChanged = useCallback((e: SelectionChangedEvent<HoldingsSecurityDetails>) => {
    const selectedNodes = e.api.getSelectedNodes();
    if (selectedNodes.length == 0) {
      clearHighlightedSecurities();
    } else {
      let ids = selectedNodes.flatMap(node => node.allLeafChildren?.map(row => row.data?.identifier).filter(v => v !== undefined) ?? []);
      let groupings: string[] = [];
      let currNode: IRowNode<HoldingsSecurityDetails> | null = selectedNodes[0];
      while (currNode?.key) {
        let groupingName = currNode.key;
        const nodeCol = currNode.rowGroupColumn;
        const nodeColDef = currNode.rowGroupColumn?.getColDef();
        const valFormatter = nodeColDef?.valueFormatter;
        if (typeof valFormatter === "function" && nodeColDef != null && nodeCol != null) {
          groupingName = valFormatter({
            value: currNode.key,
            node: currNode,
            column: nodeCol,
            colDef: nodeColDef,
            context: nodeColDef.context,
            api: e.api,
            data: {}
          });
        }
        groupings.unshift(groupingName);
        currNode = currNode.parent;
      }
      highlightSecurities(ids);
      setHighlightGroups(...groupings);
    }
  }, []);

  const agGridContext = useMemo(() => {
    return {
      baseCCY
    };
  }, [baseCCY]);

  const cardLayout = useMemo(() => ({
    width: '100%',
    height: collapsed ? 'auto' : '100%',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
    padding: '12px',
    boxSizing: 'border-box' as const,
  }), [collapsed])

  return (
    <Stack
      sx={{
        ...cardStyle,
        ...cardLayout,
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}
    >
      {/* Card header */}
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='flex-start'
        sx={{ mb: collapsed ? 0 : '10px' }}
      >
        <Stack>
          <Typography variant='subtitle2' sx={{ fontWeight: 700, color: theme.textPrimary }}>
            {CardTitleLookup[props.cardId]}
          </Typography>
          {!collapsed && (
            <Typography sx={{ fontSize: '14px', color: theme.textSecondary }}>
              Detailed view of securities
            </Typography>
          )}
        </Stack>
        <Stack direction='row' alignItems='center'>
          {/* Export menu (3-dot) — CSV / Excel of the holdings table */}
          {!collapsed && (
            <>
              <IconButton
                size='small'
                onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                sx={{ color: theme.textSecondary, padding: '4px' }}
              >
                <MoreVertIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Menu
                anchorEl={exportMenuAnchor}
                open={Boolean(exportMenuAnchor)}
                onClose={() => setExportMenuAnchor(null)}
                PaperProps={{
                  sx: {
                    backgroundColor: theme.cardBackground,
                    border: `1px solid ${theme.border}`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    minWidth: 140,
                  },
                }}
              >
                <MenuItem
                  onClick={() => handleExport('EXCEL')}
                  sx={{
                    fontSize: '14px',
                    color: theme.textPrimary,
                    '&:hover': { backgroundColor: theme.border },
                  }}
                >
                  Export as Excel
                </MenuItem>
                <MenuItem
                  onClick={() => handleExport('CSV')}
                  sx={{
                    fontSize: '14px',
                    color: theme.textPrimary,
                    '&:hover': { backgroundColor: theme.border },
                  }}
                >
                  Export as CSV
                </MenuItem>
              </Menu>
            </>
          )}
          <IconButton
            size='small'
            onClick={() => setCollapsed(p => !p)}
            sx={{ color: theme.textSecondary, padding: '4px' }}
          >
            {collapsed
              ? <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
              : <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />}
          </IconButton>
        </Stack>
      </Stack>

      {!collapsed && (
        <Stack sx={{ flex: 1, minHeight: 0, overflow: 'hidden', gap: '8px' }}>

          {/* ── ag-Grid ── */}
          <Box
            ref={gridBoxRef}
            sx={[
              {
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                // cwan theme drives colors; keep grid-specific tokens only.
                '--ag-secondary-foreground-color': theme.textSecondary,
                '--ag-row-hover-color': getColorWithOpacity(theme.border, 0.4),
                '--ag-font-size': '14px',
                '--ag-grid-size': '4px',
                '--ag-list-item-height': '28px',
                '--ag-cell-horizontal-padding': '10px',
                '--ag-chip-background-color': theme.border + '88',
                '--ag-control-panel-background-color': theme.cardBackground,
                '--ag-subheader-background-color': theme.cardBackground,
                '--ag-input-border-color': theme.border,
                '--ag-modal-overlay-background-color': getColorWithOpacity(theme.cardBackground, 0.66),
                // Row group panel (drag zone) styling
                '& .ag-row-group-panel': {
                  borderBottom: `1px dashed ${theme.border}`,
                  padding: '4px 10px',
                  minHeight: '36px',
                  display: 'flex',
                  alignItems: 'center',
                },
                '& .ag-column-drop-empty-message': {
                  fontSize: '14px',
                  color: theme.textSecondary,
                },
                // Grand total row (level -1 footer)
                '& .ag-row-level--1.ag-row-footer': {
                  backgroundColor: theme.border + 'bb',
                  fontWeight: '700',
                  fontStyle: 'normal',
                  borderTop: `2px solid ${theme.border}`,
                },
              } as React.CSSProperties,
              gridHeaderIconsSx,
            ]}
            className={getGridThemeClass(isDarkMode)}
          >
            <AgGridReact<HoldingsSecurityDetails>
              ref={gridRef}
              containerStyle={{ height: '100%', width: '100%' }}
              rowData={securityDetails ?? EMPTY_ARRAY}
              columnDefs={COLUMN_DEFS}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              groupDisplayType='singleColumn'
              groupDefaultExpanded={-1}
              rowGroupPanelShow='always'
              grandTotalRow='bottom'
              suppressAggFuncInHeader
              sideBar={HoldingsSidebarDef}
              suppressRowGroupHidesColumns
              suppressDragLeaveHidesColumns
              columnHoverHighlight
              enableRangeSelection
              popupParent={document.body}
              getContextMenuItems={gridContextMenuItems}
              processCellForClipboard={rawCellValue}
              icons={{ filter: () => FILTER_ICON_SVG }}
              animateRows={false}
              onGridReady={onGridReady}
              onColumnRowGroupChanged={onColumnRowGroupChanged}
              onSortChanged={saveGridState}
              onColumnMoved={onColumnMoved}
              onColumnPinned={saveGridState}
              onColumnResized={onColumnResized}
              onToolPanelVisibleChanged={onToolPanelVisibleChanged}
              aggFuncs={aggFuncs}
              onSelectionChanged={onRowSelectionChanged}
              context={agGridContext}
              tooltipShowDelay={200}
            />
          </Box>
        </Stack>
      )}
    </Stack>
  )
}

export default PortfolioHoldings
